import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { useTermoNotificacao } from "@/hooks/useTermoNotificacao";
import { IListaTermoNotificacao } from "@/interfaces/IListaTermoNotificacao";
import { ITermoNotificacao } from "@/interfaces/ITermoNotificacao";
import { ImpressaoTermoNotificacao } from "@/pages/termoNotificacao/_components/ImpressaoTermoNotificacao";
import { ModalCancelarTermoNotificacao } from "@/pages/termoNotificacao/_components/ModalCancelarTermoNotificacao";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function ItemListFiltroTermoNotificacao({
  item,
  reloadItems,
}: {
  item: IListaTermoNotificacao["content"][number];
  reloadItems: () => void;
}) {
  const { isOnline } = useNetworkStatus();
  const termoRef = useRef<HTMLDivElement>(null);
  const {
    getTermoNotificacaoByIdApi,
    addTermoNotificacao,
    listaTermosNotificacao,
  } = useTermoNotificacao();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message } = App.useApp();
  const navigate = useNavigate();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);
  const [termoNotificacao, setTermoNotificacao] =
    useState<ITermoNotificacao | null>(null);

  const redirectToView = () => {
    navigate(`/termos-notificacao/visualizar/${item.id}`);
  };

  const handlePrint = useReactToPrint({
    contentRef: termoRef,
    documentTitle: "termo_de_notificacao",
    suppressErrors: true,
    preserveAfterPrint: true,
  });

  const handleDownload = async () => {
    try {
      if (listaTermosNotificacao.find((termo) => termo.id === item.id)) {
        notification.info({
          message: "Este Termo de Notificação já foi baixado localmente.",
          key: "info",
        });
        return;
      }

      message.loading({
        content: "Baixando termo de notificação...",
        key: "loading",
        duration: 0,
      });
      setIsLoadingDownload(true);

      const termo = await getTermoNotificacaoByIdApi(item.id!);
      setIsLoadingDownload(false);
      message.destroy("loading");

      addTermoNotificacao(termo);
      notification.success({
        message: "Termo de notificação baixado com sucesso!",
        key: "success",
      });
    } catch (error) {
      console.error("Erro ao baixar o termo de notificação:", error);
      message.destroy("loading");
      setIsLoadingDownload(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: " Não foi possível baixar o termo de notificação",
        key: "error",
        description: messageError,
      });
    }
  };

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  const handleMountTermo = async () => {
    const termo = await getTermoNotificacaoByIdApi(item.id!);
    setTermoNotificacao(termo);
  };

  useEffect(() => {
    if (termoNotificacao) {
      handlePrint();
    }
  }, [termoNotificacao, handlePrint]);

  return (
    <>
      <List.Item
        extra={
          <Space>
            <Tooltip title="Baixar Para uso de campo" destroyOnHidden>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={
                  isLoadingDownload || !isOnline || item.status === "CANCELADO"
                }
                loading={isLoadingDownload}
              />
            </Tooltip>

            <Tooltip title="Imprimir" destroyOnHidden>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={handleMountTermo}
                disabled={!isOnline || item.status === "CANCELADO"}
              />
            </Tooltip>

            <Tooltip title="Visualizar" destroyOnHidden>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={redirectToView}
                disabled={
                  !isOnline ||
                  item.status === "CANCELADO" ||
                  !hasEnabledPermissao("TERMO_NOTIFICACAO_VISUALIZAR")
                }
              />
            </Tooltip>

            <Tooltip title="Cancelar" destroyOnHidden>
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={handleOpenModalCancelar}
                disabled={
                  !isOnline ||
                  item.status === "CANCELADO" ||
                  !hasEnabledPermissao("TERMO_NOTIFICACAO_CANCELAR")
                }
              />
            </Tooltip>
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Typography.Text
              strong
              style={{
                color: isOpenModalCancelar ? "#d7263d" : undefined,
                opacity: item.status === "CANCELADO" ? 0.6 : 1,
              }}
            >
              Termo Notificação {item.numero}
            </Typography.Text>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Estabelecimento: {item.estabelecimento.numeroInspecao}-
              {TIPO_INSPECAO[item.estabelecimento.tipoInspecao]}-
              {item.estabelecimento.nome}
            </Typography.Text>
          }
        />
      </List.Item>

      <div style={{ display: "none" }}>
        <ImpressaoTermoNotificacao
          marcadagua=""
          ref={termoRef}
          item={termoNotificacao}
        />
      </div>

      <ModalCancelarTermoNotificacao
        termoNotificacaoId={item.id!}
        isOpen={isOpenModalCancelar}
        onClose={() => {
          setIsOpenModalCancelar(false);
        }}
        reloadItems={reloadItems}
      />
    </>
  );
}
